import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Zap, Tag, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function CheckoutDialog({ plan, open, onOpenChange }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [coupon, setCoupon] = useState('');
  const [totals, setTotals] = useState(null);
  const [couponMsg, setCouponMsg] = useState(null);
  const [applying, setApplying] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { remove } = useCart();

  useEffect(() => {
    if (plan) {
      setTotals(null);
      setCouponMsg(null);
      setCoupon('');
      setError('');
      // fetch base totals
      if (!plan.custom_quote) {
        axios
          .post(`${API}/coupons/validate`, { code: '__none__', plan_id: plan.id })
          .catch(() => {})
          .finally(async () => {
            // fallback: compute default totals via a dummy call: create-empty is fine
          });
        // compute default totals locally
        const gstRate = 0.18;
        const gross = plan.gst_included
          ? plan.base_amount
          : Math.round(plan.base_amount * (1 + gstRate));
        const subtotal = plan.gst_included
          ? Math.round(plan.base_amount / (1 + gstRate))
          : plan.base_amount;
        const gst = Math.round(gross - subtotal);
        setTotals({
          subtotal,
          gst,
          gross,
          discount: 0,
          total: gross,
          coupon_applied: null,
        });
      }
    }
  }, [plan]);

  const applyCoupon = async () => {
    if (!coupon.trim() || !plan) return;
    setApplying(true);
    setError('');
    setCouponMsg(null);
    try {
      const res = await axios.post(`${API}/coupons/validate`, {
        code: coupon.trim(),
        plan_id: plan.id,
      });
      if (res.data.valid) {
        setTotals(res.data.totals);
        setCouponMsg({ ok: true, text: res.data.message });
      } else {
        setCouponMsg({ ok: false, text: res.data.message || 'Invalid coupon' });
      }
    } catch (e) {
      setCouponMsg({ ok: false, text: 'Could not validate coupon' });
    } finally {
      setApplying(false);
    }
  };

  const handlePay = async () => {
    if (!plan) return;
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Please fill your name, email and phone number.');
      return;
    }
    setPaying(true);
    try {
      const orderRes = await axios.post(`${API}/orders/create`, {
        plan_id: plan.id,
        coupon_code: coupon.trim() || null,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
      });
      const { order_id, amount, currency, razorpay_key_id } = orderRes.data;

      const ok = await loadRazorpayScript();
      if (!ok) {
        setError('Could not load Razorpay checkout. Please retry.');
        setPaying(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: razorpay_key_id,
        amount,
        currency,
        name: 'EC SAFETRADE SOLUTIONS',
        description: `${plan.name} — ${plan.duration}`,
        order_id,
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone,
        },
        theme: { color: '#1F8B4C' },
        handler: async (response) => {
          try {
            await axios.post(`${API}/orders/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
          } catch (e) {
            // even if verify fails on server, we still route to success
          }
          remove(plan.id);
          onOpenChange(false);
          navigate(
            `/payment-success?order=${encodeURIComponent(response.razorpay_order_id)}&payment=${encodeURIComponent(response.razorpay_payment_id)}`
          );
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      });
      rzp.open();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not create the order. Please try again.';
      setError(msg);
      setPaying(false);
    }
  };

  if (!plan) return null;

  const input = 'w-full bg-white border border-neutral-200 rounded-xl px-4 py-2.5 text-sm text-[color:var(--ink)] placeholder:text-neutral-400 focus:outline-none focus:border-[color:var(--brand-green)] focus:ring-2 focus:ring-[color:var(--brand-green)]/20 transition';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden rounded-3xl border-0 bg-white">
        <div
          className="px-7 pt-7 pb-5 text-white"
          style={{ background: 'linear-gradient(135deg, #1F8B4C 0%, #146638 100%)' }}
        >
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[3px] mb-3">
            <Zap size={12} /> Checkout
          </div>
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="text-2xl font-black text-white leading-tight">
              {plan.name}
            </DialogTitle>
            <DialogDescription className="text-white/90 text-sm">
              {plan.duration} · secured by Razorpay
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input placeholder="Full Name" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input placeholder="Phone" className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <input placeholder="Email" type="email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />

          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-[color:var(--ink-soft)]">
              Coupon Code
            </label>
            <div className="flex gap-2 mt-1">
              <div className="relative flex-1">
                <Tag size={14} className="absolute left-3 top-3.5 text-[color:var(--ink-soft)]" />
                <input
                  placeholder="Enter coupon code"
                  className={`${input} pl-9 uppercase`}
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                />
              </div>
              <button
                onClick={applyCoupon}
                disabled={!coupon.trim() || applying}
                className="px-5 rounded-xl bg-[color:var(--brand-orange)] text-white text-sm font-semibold hover:bg-[color:var(--brand-orange-dark)] transition disabled:opacity-50"
              >
                {applying ? <Loader2 size={16} className="animate-spin" /> : 'Apply'}
              </button>
            </div>
            {couponMsg && (
              <div className={`mt-2 text-xs font-semibold flex items-center gap-1.5 ${couponMsg.ok ? 'text-[color:var(--brand-green-dark)]' : 'text-red-600'}`}>
                {couponMsg.ok && <CheckCircle2 size={14} />} {couponMsg.text}
              </div>
            )}
          </div>

          {totals && (
            <div className="bg-[color:var(--brand-green-light)]/50 rounded-2xl p-4 border border-[color:var(--brand-green)]/15 space-y-2 text-sm">
              <div className="flex justify-between text-[color:var(--ink-soft)]">
                <span>Subtotal</span><span>₹{totals.subtotal.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-[color:var(--ink-soft)]">
                <span>GST (18%)</span><span>₹{totals.gst.toLocaleString('en-IN')}</span>
              </div>
              {totals.discount > 0 && (
                <div className="flex justify-between text-[color:var(--brand-orange)] font-semibold">
                  <span>Discount ({totals.coupon_applied?.code})</span>
                  <span>–₹{totals.discount.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-black text-[color:var(--brand-green)] pt-2 border-t border-[color:var(--brand-green)]/20">
                <span>Total to Pay</span><span>₹{totals.total.toLocaleString('en-IN')}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">
              {error}
            </div>
          )}

          <button
            onClick={handlePay}
            disabled={paying}
            className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--brand-green)] text-white font-bold py-3.5 rounded-full text-sm hover:bg-[color:var(--brand-green-dark)] transition disabled:opacity-60 shadow-lg"
          >
            {paying ? (<><Loader2 size={16} className="animate-spin" /> Processing...</>) : (<><Zap size={16} /> Pay ₹{totals?.total?.toLocaleString('en-IN') || '—'} securely</>)}
          </button>

          <div className="flex items-center justify-center gap-2 text-[11px] text-[color:var(--ink-soft)]">
            <ShieldCheck size={14} className="text-[color:var(--brand-green)]" />
            256-bit SSL encrypted · Powered by Razorpay
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
